<main class="pb-10">
  <div class="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-4">

    @if (loading) {
      <!-- Loading -->
      <div class="flex items-center justify-center py-24">
        <div class="relative h-12 w-12">
          <div class="absolute inset-0 rounded-full border-2 border-theme-primary-100"></div>
          <div class="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-theme-primary-500"></div>
        </div>
      </div>
    } @else if (errored) {
      <!-- Error -->
      <div class="rounded-3xl border border-red-100 bg-red-50/70 p-5">
        <div class="flex items-start gap-3">
          <svg aria-hidden="true" class="h-5 w-5 flex-none text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
          </svg>
          <div class="min-w-0">
            <p class="text-sm font-bold tracking-tight text-gray-800">Dashboard konnte nicht geladen werden.</p>
            <button (click)="load()"
                    class="mt-2 inline-flex items-center rounded-lg bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 active:scale-95">
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    } @else if (data) {

      <!-- ================= HERO (pinned below the navbar — stays put while everything scrolls) ============ -->
      <div class="relative z-20 lg:sticky sm:top-2 sm:-mt-4">
        <!-- Background "cap": fills the gap between the navbar and the pinned hero so events scroll *behind* it.
             Only at sm+ (where the hero is sticky); the shell's overflow clips it to the navbar's bottom edge. -->
        <div aria-hidden="true" class="pointer-events-none absolute inset-x-0 -bottom-14 hidden h-[calc(var(--hero-h)+6rem)] -mx-4 bg-gray-50 sm:-mx-6 sm:block lg:-mx-8"></div>
        <section data-hero
                 class="dash-rise relative overflow-hidden rounded-3xl bg-gray-950 px-5 pt-6 pb-14 text-white shadow-2xl sm:px-8 sm:pt-8 sm:pb-16">
        <!-- atmosphere: drifting glow blobs + grid mesh.
             `clip-path` matching the hero's own `rounded-3xl` (1.5rem), ON THIS LAYER rather than on the hero.
             The blobs are `blur-3xl`, which promotes them to their own compositing layer, and a composited
             layer is the classic case where an ancestor's `overflow-hidden` clips to the BOX but not reliably
             to the border-RADIUS — the reported symptom is a pink wedge in the hero's top-left notch on Safari.
             `clip-path` clips to the rounded shape explicitly, in every engine.
             It must NOT go on the hero itself: `clip-path` clips an element's own box-shadow too, and measured
             beside the hero that turned `rgb(233,234,235)` into `rgb(249,250,251)` — i.e. it deleted the
             `shadow-2xl`. `-webkit-mask-image` does the same. This wrapper casts no shadow, so clipping it
             costs nothing. Measured at 390x844: tinted pixels outside the arc 4 → 0 in Chromium, 0 → 0 in
             Playwright's WebKit, with the hero's shadow unchanged. -->
        <div aria-hidden="true" class="pointer-events-none absolute inset-0 [clip-path:inset(0_round_1.5rem)]">
          <div class="dash-float absolute -left-20 -top-24 h-72 w-72 rounded-full bg-theme-primary-500/30 blur-3xl"></div>
          <div class="dash-float-slow absolute -right-16 top-4 h-80 w-80 rounded-full bg-theme-primary-700/25 blur-3xl"></div>
          <div class="absolute inset-0 opacity-[0.06]"
               style="background-image:linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px);background-size:38px 38px;-webkit-mask-image:radial-gradient(ellipse at top left,#000,transparent 75%);mask-image:radial-gradient(ellipse at top left,#000,transparent 75%)"></div>
          <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent"></div>
        </div>

        <div class="relative">
          <div class="flex items-center gap-2 text-theme-primary-300/90">
            <svg aria-hidden="true" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75"/>
            </svg>
            <span class="text-xs font-semibold uppercase tracking-[0.22em]">Übersicht · {{ today | date : "EEEE, dd.MM.yyyy" }}</span>
          </div>

          @if (data.currentEvent; as ev) {
            <!-- Live event, centre stage.
                 #664: this is ALWAYS a Kassen-Event — the server's `CurrentEvent` is real-only — so the hero
                 carries no „Virtuell" pill and must never grow one. „Jetzt live" is a statement about an open
                 till; a planning row here would announce trading that is not happening. A virtual event that
                 is running right now is listed under „Kommende Events" instead, marked with its pill. -->
            <div
              [routerLink]="canOpenEvents ? ['/' + Routes.LocationModule + '/' + LocationRoutes.EventDetails] : null"
              [queryParams]="{ id: ev.id }"
              queryParamsHandling="merge"
              [class.cursor-pointer]="canOpenEvents"
              class="group mt-4">
              <div class="flex items-center gap-2">
                <span class="relative flex h-2.5 w-2.5">
                  <span class="dash-breathe absolute inline-flex h-full w-full rounded-full bg-theme-primary-400"></span>
                  <span class="relative inline-flex h-2.5 w-2.5 rounded-full bg-theme-primary-400 ring-2 ring-theme-primary-400/40"></span>
                </span>
                <span class="text-xs font-bold uppercase tracking-[0.18em] text-theme-primary-200">Jetzt live</span>
              </div>
              <h1 class="mt-2 text-3xl font-bold leading-tight tracking-tight transition-colors group-hover:text-theme-primary-100 sm:text-4xl">
                {{ ev.name }}
              </h1>
              <div class="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
                <span class="inline-flex items-center gap-1.5">
                  <svg aria-hidden="true" class="h-4 w-4 text-theme-primary-300" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  {{ ev.start | date : "HH:mm" }} – {{ ev.end | date : "HH:mm" }} Uhr
                </span>
              </div>
              <!-- elapsed progress with light sweep -->
              <div class="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-white/15">
                <div class="relative h-full overflow-hidden rounded-full bg-gradient-to-r from-theme-primary-400 to-theme-primary-200 transition-all duration-700"
                     [style.width.%]="currentProgress">
                  <span class="dash-shimmer absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/70 to-transparent"></span>
                </div>
              </div>
            </div>
            <!-- quick action: shift maker for this event (Personalplaner / Schichtleiter / admin). A sibling of
                 the (clickable) live-event block, so it doesn't trip the event-details navigation. Top-right on lg. -->
            @if (canPlanShifts) {
              <a [routerLink]="['/' + Routes.CompanyModule + '/' + CompanyRoutes.ShiftMaker]"
                 [queryParams]="{ event: ev.id, date: ev.start }"
                 class="mt-5 inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-lg shadow-black/20 transition-all duration-200 hover:bg-theme-primary-50 hover:text-theme-primary-700 active:scale-95 lg:absolute lg:right-0 lg:top-0 lg:mt-0">
                <svg aria-hidden="true" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"/>
                </svg>
                Dienstplan öffnen
              </a>
            }
          } @else {
            <!-- No live event -->
            <h1 class="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">Es läuft gerade kein Event</h1>
            @if (data.upcomingEvents[0]; as next) {
              <p class="mt-2 text-sm text-white/60">
                Als Nächstes:
                <span class="font-semibold text-white/90">{{ next.name }}</span>
                <span class="text-white/60"> · {{ next.start | date : "dd.MM.yyyy, HH:mm" }} Uhr</span>
              </p>
            } @else {
              <p class="mt-2 text-sm text-white/60">Keine geplanten Events.</p>
            }
          }
        </div>
        </section>
      </div>

      <!-- ================= STATS (float above the hero edge; fade + drift away as the page scrolls) ========
           Each tile's decorative glyph sits INSIDE its padding box (`right-3 top-3`), not bleeding out of it
           (`-right-3 -top-3`). It used to fill the tile's rounded top-right corner, and these tiles are pulled
           up over the hero by the negative margin — so that pink corner sat directly against the dark hero and
           read as "a pink corner on the card". Measured at 390x844 it was `rgb(253,242,248)`
           (theme-primary-50) across 37.7 % of a 48 px square at the hero's bottom-right, IDENTICALLY in
           Chromium and WebKit: a layout collision, not an engine bug. Inside the padding box the corner is the
           tile's own white surface and the decoration is unchanged everywhere else. -->
      <section data-stats class="relative z-30 -mt-8 grid grid-cols-1 gap-4 will-change-[opacity,transform] sm:-mt-10 sm:grid-cols-3">
        <!-- Items -->
        <div class="dash-rise group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-lg shadow-gray-900/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
             [style.animation-delay.ms]="80">
          <svg aria-hidden="true" class="pointer-events-none absolute right-3 top-3 h-20 w-20 text-theme-primary-50 transition-transform duration-500 group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24">
            <path d="M21 7.5l-9-5.25L3 7.5v9L12 21.75 21 16.5v-9zM12 4.65l5.66 3.3L12 11.25 6.34 7.95 12 4.65z"/>
          </svg>
          <div class="relative">
            <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-theme-primary-50 text-theme-primary-600">
              <svg aria-hidden="true" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"/>
              </svg>
            </div>
            <dl class="mt-3 flex flex-col-reverse">
              <dt class="text-sm font-medium text-gray-500">Anzahl Items</dt>
              <dd class="text-3xl font-bold tracking-tight text-gray-900">{{ data.stats.itemCount }}</dd>
            </dl>
            @if (PermissionService.CheckPermission(PermissionService.Permissions_ItemComponent())) {
              <a [routerLink]="['/items']" class="mt-2 inline-block text-xs font-semibold text-theme-primary-700 hover:text-theme-primary-900">Alle anzeigen&nbsp;&rarr;</a>
            }
          </div>
        </div>

        <!-- Locations -->
        <div class="dash-rise group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-lg shadow-gray-900/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
             [style.animation-delay.ms]="160">
          <svg aria-hidden="true" class="pointer-events-none absolute right-3 top-3 h-20 w-20 text-theme-primary-50 transition-transform duration-500 group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24">
            <path d="M4 10v9a1 1 0 001 1h4v-6h6v6h4a1 1 0 001-1v-9l-8-6-8 6z"/>
          </svg>
          <div class="relative">
            <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-theme-primary-50 text-theme-primary-600">
              <svg aria-hidden="true" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72"/>
              </svg>
            </div>
            <dl class="mt-3 flex flex-col-reverse">
              <dt class="text-sm font-medium text-gray-500">Anzahl Verkaufspunkte</dt>
              <dd class="text-3xl font-bold tracking-tight text-gray-900">{{ data.stats.locationCount }}</dd>
            </dl>
            @if (PermissionService.CheckPermission(PermissionService.Permissions_LocationsComponent())) {
              <a [routerLink]="['/locations']" class="mt-2 inline-block text-xs font-semibold text-theme-primary-700 hover:text-theme-primary-900">Alle anzeigen&nbsp;&rarr;</a>
            }
          </div>
        </div>

        <!-- Events -->
        <div class="dash-rise group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-lg shadow-gray-900/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
             [style.animation-delay.ms]="240">
          <svg aria-hidden="true" class="pointer-events-none absolute right-3 top-3 h-20 w-20 text-theme-primary-50 transition-transform duration-500 group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 00-2 2v13a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zm0 15H5V9h14v10z"/>
          </svg>
          <div class="relative">
            <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-theme-primary-50 text-theme-primary-600">
              <svg aria-hidden="true" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"/>
              </svg>
            </div>
            <dl class="mt-3 flex flex-col-reverse">
              <!-- #664: the count is REAL events only, so the label names them. „Anzahl Events" was fine while
                   the whole dashboard was real-only; beside two timelines that now list planning rows too, it
                   would read as a number that disagrees with the page under it. -->
              <dt class="text-sm font-medium text-gray-500">Anzahl Kassen-Events</dt>
              <dd class="text-3xl font-bold tracking-tight text-gray-900">{{ data.stats.eventCount }}</dd>
            </dl>
            @if (PermissionService.CheckPermission(PermissionService.Permissions_EventsComponent())) {
              <a [routerLink]="['/' + Routes.LocationModule + '/' + LocationRoutes.Events]" class="mt-2 inline-block text-xs font-semibold text-theme-primary-700 hover:text-theme-primary-900">Alle anzeigen&nbsp;&rarr;</a>
            }
          </div>
        </div>
      </section>

      <!-- ================= EVENT TIMELINES (page scrolls; headers pin below the hero) ============= -->
      <div class="mt-6">
        <!-- Desktop: two columns; each header sticks right below the (also sticky) hero. -->
        <div class="hidden lg:grid lg:grid-cols-2 lg:gap-6">
          <section>
            <ng-container *ngTemplateOutlet="pane; context: { events: data.upcomingEvents, kind: 'upcoming' }"></ng-container>
          </section>
          <section>
            <ng-container *ngTemplateOutlet="pane; context: { events: data.pastEvents, kind: 'past' }"></ng-container>
          </section>
        </div>

        <!-- Small screens: segmented control + animated slide between the two timelines -->
        <div class="lg:hidden sm:sticky">
          <div class="mb-3 flex w-full rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
            <button type="button" (click)="mobileTab = 'upcoming'"
                    class="flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all duration-200"
                    [class]="mobileTab === 'upcoming' ? 'bg-theme-primary-50 text-theme-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-900'">
              Kommend
            </button>
            <button type="button" (click)="mobileTab = 'past'"
                    class="flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all duration-200"
                    [class]="mobileTab === 'past' ? 'bg-theme-primary-50 text-theme-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-900'">
              Vergangen
            </button>
          </div>
          <div class="overflow-hidden">
            <div class="flex w-[200%] transition-transform duration-300 ease-out"
                 [style.transform]="mobileTab === 'past' ? 'translateX(-50%)' : 'translateX(0%)'">
              <div class="w-1/2 shrink-0 pr-2">
                <ng-container *ngTemplateOutlet="pane; context: { events: data.upcomingEvents, kind: 'upcoming' }"></ng-container>
              </div>
              <div class="w-1/2 shrink-0 pl-2">
                <ng-container *ngTemplateOutlet="pane; context: { events: data.pastEvents, kind: 'past' }"></ng-container>
              </div>
            </div>
          </div>
        </div>
      </div>

    }
  </div>
</main>

<!-- ================= Reusable timeline pane (upcoming | past) ================= -->
<ng-template #pane let-events="events" let-kind="kind">
  <!-- On lg the header pins right below the sticky hero (offset = the hero's measured height, `--hero-h`). -->
  <div class="mb-3 flex items-center justify-between gap-3 lg:sticky lg:top-[calc(var(--hero-h)+1rem)] lg:z-20 lg:-mx-1 lg:bg-transparent lg:px-1 lg:pb-2 lg:pt-3">
    <h2 class="flex items-center gap-2 text-base font-bold tracking-tight text-gray-800">
      <span class="h-2 w-2 rounded-full" [class]="kind === 'upcoming' ? 'bg-theme-primary-500' : 'bg-gray-300'"></span>
      {{ kind === 'upcoming' ? 'Kommende Events' : 'Vergangene Events' }}
    </h2>
    @if (canViewAllEvents) {
      <a [routerLink]="['/' + Routes.LocationModule + '/' + LocationRoutes.Events]"
         class="select-none text-sm font-medium text-theme-primary-700 hover:text-theme-primary-900">Alle anzeigen</a>
    }
  </div>

  <ol class="relative ml-1.5 space-y-2 before:absolute before:left-[5px] before:top-2 before:bottom-2 before:w-px before:bg-gradient-to-b before:to-transparent"
      [class]="kind === 'upcoming' ? 'before:from-theme-primary-200 before:via-theme-primary-100' : 'before:from-gray-200'">
    @for (ev of events; track ev.id; let i = $index) {
      <li class="dash-rise relative pl-7" [style.animation-delay.ms]="120 + i * 55">
        <span class="absolute left-0 top-4 h-[11px] w-[11px] rounded-full border-2 border-white"
              [class]="kind === 'upcoming' ? 'bg-theme-primary-500 shadow-[0_0_0_3px_rgba(236,72,153,0.18)]' : 'bg-gray-300'"></span>
        <div class="group flex items-center justify-between gap-3 rounded-2xl border border-gray-100 px-4 py-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
             [class]="kind === 'upcoming' ? 'bg-white hover:border-theme-primary-100' : 'bg-white/70 hover:bg-white'">
          <a
            [routerLink]="canOpenEvents ? ['/' + Routes.LocationModule + '/' + LocationRoutes.EventDetails] : null"
            [queryParams]="{ id: ev.id }"
            queryParamsHandling="merge"
            [class.cursor-pointer]="canOpenEvents"
            class="min-w-0 flex-1">
            <p class="truncate text-sm font-semibold" [class]="kind === 'upcoming' ? 'text-gray-900' : 'text-gray-700'">{{ ev.name }}</p>
            <p class="mt-0.5 text-xs text-gray-500">{{ ev.start | date : "dd.MM.yyyy, HH:mm" }} Uhr</p>
          </a>
          <div class="flex flex-none items-center gap-2">
            <!-- #664: what the row IS comes before what it is shared as. Indigo in BOTH timelines, unlike the
                 „Öffentlich" pill beside it, which dims in the past list: the kind is a property of the event,
                 not of its recency, and indigo is the product-wide marker for it (events list, shift plan,
                 employee calendar). `align-top` because an inline-flex chip beside larger text otherwise hangs
                 ~1.5px low (style guide, "Status badge / pill"). -->
            @if (ev.kind === EventKind.Virtual) {
              <span class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 align-top text-xs
                           font-semibold text-indigo-700"
                    data-testid="event-kind-badge">{{ VirtualBadge }}</span>
            }
            @if (ev.public) {
              <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                    [class]="kind === 'upcoming' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'">Öffentlich</span>
            }
            @if (canPlanShifts) {
              <a [routerLink]="['/' + Routes.CompanyModule + '/' + CompanyRoutes.ShiftMaker]"
                 [queryParams]="{ event: ev.id, date: ev.start }"
                 title="Dienstplan öffnen" aria-label="Dienstplan öffnen"
                 class="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-theme-primary-50 hover:text-theme-primary-600">
                <svg aria-hidden="true" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"/>
                </svg>
              </a>
            }
          </div>
        </div>
      </li>
    } @empty {
      <li class="rounded-2xl border-2 border-dashed border-gray-100 py-8 text-center text-sm italic text-gray-500">
        {{ kind === 'upcoming' ? 'Keine kommenden Events.' : 'Keine vergangenen Events.' }}
      </li>
    }
  </ol>
</ng-template>
